import React, { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectUnitToken } from '@homeplay/app';
import {
  apiGetPlayerBets,
  apiGetPlayerTickets,
  apiCancelBet,
  apiCancelSubscription,
} from '@homeplay/api';
import BetCard from './components/BetCard';
import TicketCard from './components/TicketCard';
import SubscriptionCard from './components/SubscriptionCard';
import { useCallOnceWhen } from '../LottoAssistant/hooks/useCallOnceWhen';
import ConfirmModal from '../../../components/src/Modal/ConfirmModal';
import { MY_BETS_TABS, PAGE_LIMIT } from './constants';
import classes from './MyBets.module.scss';

const MyBets = ({ onWidgetDataLoaded, dateFilter, trackLottoEvent }) => {
  const token = useSelector(selectUnitToken);

  const [activeTab, setActiveTab] = useState('tickets');
  const [bets, setBets] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [ticketSlips, setTicketSlips] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [isTabSwitching, setIsTabSwitching] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isTicketsFetching, setIsTicketsFetching] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isTicketsError, setIsTicketsError] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [cancelErrorId, setCancelErrorId] = useState(null);
  const [cancellingSubscriptionId, setCancellingSubscriptionId] = useState(null);
  const [cancelSubscriptionErrorId, setCancelSubscriptionErrorId] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);

  const dateFrom = dateFilter?.from ?? null;
  const dateTo = dateFilter?.to ?? null;

  useCallOnceWhen(initialLoaded, onWidgetDataLoaded);

  const loadBets = useCallback(
    async (status, cursor) => {
      if (!token) return;

      try {
        if (status === 'tickets') {
          setIsTicketsFetching(true);
          setIsTicketsError(false);
          const result = await apiGetPlayerTickets({
            unitToken: token,
            limit: PAGE_LIMIT,
            cursor,
            from: dateFrom ?? null,
            to: dateTo ?? null,
          });
          const newSlips = result?.bets ?? [];
          if (cursor) {
            setTicketSlips((prev) => [...prev, ...newSlips]);
          } else {
            setTicketSlips(newSlips);
            setIsTabSwitching(false);
          }
          setNextCursor(result?.meta?.end_cursor ?? null);
          setHasNextPage(result?.meta?.has_next_page ?? false);
          if (!initialLoaded) setInitialLoaded(true);
          return;
        }

        setIsFetching(true);
        setIsError(false);
        const result = await apiGetPlayerBets({
          unitToken: token,
          status,
          limit: PAGE_LIMIT,
          cursor,
          from: dateFrom ?? null,
          to: dateTo ?? null,
        });
        const newItems = result?.bets ?? [];

        if (status === 'subscriptions') {
          if (cursor) {
            setSubscriptions((prev) => [...prev, ...newItems]);
          } else {
            setSubscriptions(newItems);
            setIsTabSwitching(false);
          }
        } else {
          if (cursor) {
            setBets((prev) => [...prev, ...newItems]);
          } else {
            setBets(newItems);
            setIsTabSwitching(false);
          }
        }
        setNextCursor(result?.meta?.end_cursor ?? null);
        setHasNextPage(result?.meta?.has_next_page ?? false);
        if (!initialLoaded) setInitialLoaded(true);
      } catch (err) {
        if (status === 'tickets') {
          setIsTicketsError(true);
        } else {
          setIsError(true);
        }
        setIsTabSwitching(false);
      } finally {
        if (status === 'tickets') {
          setIsTicketsFetching(false);
        } else {
          setIsFetching(false);
        }
      }
    },
    [token, dateFrom, dateTo, initialLoaded]
  );

  useEffect(() => {
    setNextCursor(null);
    setHasNextPage(false);
    setIsTabSwitching(true);
    setBets([]);
    setSubscriptions([]);
    setTicketSlips([]);
    loadBets(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, token, dateFrom, dateTo]);

  const handleCancelBet = useCallback(
    async (wagerSetRef) => {
      if (!token) return;
      setCancellingId(wagerSetRef);
      setCancelErrorId(null);
      try {
        const result = await apiCancelBet({
          unitToken: token,
          wagerSetId: wagerSetRef,
        });
        if (result?.success) {
          setBets((prev) => prev.filter((b) => b.wager_set_ref !== wagerSetRef));
          const bet = result.cancelled_bets?.[0];
          trackLottoEvent?.('bet_cancelled', {
            type: 'bet',
            bet_slip_id: bet?.bet_slip_id ?? null,
            tickets: (result.cancelled_bets ?? []).map((b) => ({
              wager_set_ref: b.wager_set_ref,
              lottery_name: b.lottery_name,
              market_type: b.market_type,
              market_title: b.market_title,
              draw_date: b.draw_date,
              draw_id: b.draw_id,
              draw_number: b.draw_number,
              stake: b.stake,
              balls: b.balls,
              outcome: b.outcome,
            })),
          });
        }
      } catch {
        setCancelErrorId(wagerSetRef);
      } finally {
        setCancellingId(null);
      }
    },
    [token, trackLottoEvent]
  );

  const handleCancelSubscription = useCallback(
    async (sequenceId) => {
      if (!token) return;
      setCancellingSubscriptionId(sequenceId);
      setCancelSubscriptionErrorId(null);
      try {
        const result = await apiCancelSubscription({
          unitToken: token,
          subscriptionId: sequenceId,
        });
        if (result?.success) {
          setSubscriptions((prev) =>
            prev.filter((s) => s.sequence_id !== sequenceId)
          );
          const firstBet = result.cancelled_bets?.[0];
          trackLottoEvent?.('bet_cancelled', {
            type: 'subscription',
            bet_slip_id: firstBet?.bet_slip_id ?? null,
            total_cancelled: (result.cancelled_bets ?? []).length,
            total_stake: (result.cancelled_bets ?? []).reduce(
              (sum, b) => sum + (b.stake ?? 0),
              0
            ),
            wager_set_refs: (result.cancelled_bets ?? []).map(
              (b) => b.wager_set_ref
            ),
            tickets: firstBet
              ? [
                  {
                    wager_set_ref: firstBet.wager_set_ref,
                    lottery_name: firstBet.lottery_name,
                    market_type: firstBet.market_type,
                    market_title: firstBet.market_title,
                    draw_date: firstBet.draw_date,
                    draw_id: firstBet.draw_id,
                    draw_number: firstBet.draw_number,
                    stake: firstBet.stake,
                    balls: firstBet.balls,
                    outcome: firstBet.outcome,
                  },
                ]
              : [],
          });
        }
      } catch {
        setCancelSubscriptionErrorId(sequenceId);
      } finally {
        setCancellingSubscriptionId(null);
      }
    },
    [token, trackLottoEvent]
  );

  const handleRequestCancelBet = useCallback((wagerSetRef) => {
    setConfirmCancel({ type: 'bet', id: wagerSetRef });
  }, []);

  const handleRequestCancelSubscription = useCallback((sequenceId) => {
    setConfirmCancel({ type: 'subscription', id: sequenceId });
  }, []);

  const handleConfirmCancel = useCallback(() => {
    if (!confirmCancel) return;
    const { type, id } = confirmCancel;
    setConfirmCancel(null);
    if (type === 'bet') {
      handleCancelBet(id);
    } else {
      handleCancelSubscription(id);
    }
  }, [confirmCancel, handleCancelBet, handleCancelSubscription]);

  const renderContent = () => {
    const loading = activeTab === 'tickets' ? isTicketsFetching : isFetching;
    const error = activeTab === 'tickets' ? isTicketsError : isError;
    const isEmpty =
      activeTab === 'tickets'
        ? ticketSlips.length === 0
        : activeTab === 'subscriptions'
        ? subscriptions.length === 0
        : bets.length === 0;

    if (loading && isEmpty) {
      return (
        <div className={classes.EmptyState}>
          <div className={classes.LoadingOverlay} style={{ position: 'static' }} />
        </div>
      );
    }

    if (error) {
      return (
        <div className={classes.EmptyState}>
          <p className={classes.EmptyText}>
            Failed to load bets. Please try again.
          </p>
        </div>
      );
    }

    switch (activeTab) {
      case 'tickets':
        return ticketSlips.length > 0 ? (
          ticketSlips.map((slip) => (
            <TicketCard key={slip.bet_slip_id} slip={slip} />
          ))
        ) : (
          <div className={classes.EmptyState}>
            <p className={classes.EmptyText}>No tickets</p>
          </div>
        );

      case 'open':
        return bets.length > 0 ? (
          bets.map((bet) => (
            <BetCard
              key={bet.wager_set_ref}
              variant="open"
              bet={bet}
              onCancel={handleRequestCancelBet}
              isCancelling={cancellingId === bet.wager_set_ref}
              cancelError={
                cancelErrorId === bet.wager_set_ref
                  ? 'Cancellation failed'
                  : null
              }
            />
          ))
        ) : (
          <div className={classes.EmptyState}>
            <p className={classes.EmptyText}>No open bets</p>
          </div>
        );

      case 'settled':
        return bets.length > 0 ? (
          bets.map((bet) => (
            <BetCard key={bet.wager_set_ref} bet={bet} variant="settled" />
          ))
        ) : (
          <div className={classes.EmptyState}>
            <p className={classes.EmptyText}>No settled bets</p>
          </div>
        );

      case 'subscriptions':
        return subscriptions.length > 0 ? (
          subscriptions.map((sub) => (
            <SubscriptionCard
              key={sub.sequence_id}
              subscription={sub}
              onCancel={handleRequestCancelSubscription}
              isCancelling={cancellingSubscriptionId === sub.sequence_id}
              cancelError={
                cancelSubscriptionErrorId === sub.sequence_id
                  ? 'Cancellation failed'
                  : null
              }
            />
          ))
        ) : (
          <div className={classes.EmptyState}>
            <p className={classes.EmptyText}>No subscriptions</p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={classes.Root}>
      <div className={classes.Tabs}>
        {MY_BETS_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setActiveTab(tab.value)}
            className={
              classes.Tab + (activeTab === tab.value ? ' ' + classes.Active : '')
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className={classes.ContentWrapper}>
        <div className={classes.Content}>
          {isTabSwitching && <div className={classes.LoadingOverlay} />}

          <div
            key={isTabSwitching ? 'loading' : activeTab}
            className={classes.FadeIn}
          >
            {renderContent()}
          </div>

          {nextCursor && hasNextPage && (
            <div className={classes.LoadMoreWrapper}>
              <button
                type="button"
                className={classes.LoadMoreButton}
                onClick={() => loadBets(activeTab, nextCursor)}
                disabled={isFetching || isTicketsFetching}
              >
                {isFetching || isTicketsFetching ? 'Loading...' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={!!confirmCancel}
        onClose={() => setConfirmCancel(null)}
        onConfirm={handleConfirmCancel}
      >
        {confirmCancel?.type === 'bet'
          ? 'Are you sure you want to cancel this bet?'
          : 'Are you sure you want to cancel this subscription?'}
      </ConfirmModal>
    </div>
  );
};

export default MyBets;
